import { useState, useEffect, useCallback } from 'react';

import {
  foldMonthly,
  foldCategories,
  monthOverMonthChange,
  windowStart,
  KANAL_CALOSC,
  type MonthlyPoint,
  type CategorySlice,
  type DailyRow,
  type ProductRow,
} from '../lib/salesMetrics';
import { supabase } from '../lib/supabase';
import { useAuth } from '../providers/AuthProvider';

// =============================================================================
// useCompanySalesMetrics — real sales aggregates for the company profile page.
//
// The profile page used to render four charts filled with one hard-coded array
// ([35, 55, 45, 70, ...]) under a small "Dane demonstracyjne" caption. This hook
// replaces that with the organization's own numbers, read from the LexShift
// sales tables that the POS sync populates:
//
//   lexshift_sales_daily          → revenue, receipts, average basket per day
//   lexshift_sales_product_daily  → revenue split by product category
//
// Both tables are RLS-scoped to the caller's organizations; the explicit
// organization_id filter is there to keep the query selective.
//
// An organization with no POS integration simply has no rows. That is reported
// as `hasData: false` so the UI can say so plainly instead of inventing a trend.
// The arithmetic itself lives in lib/salesMetrics.ts.
// =============================================================================

export type { MonthlyPoint, CategorySlice } from '../lib/salesMetrics';

export interface CompanySalesMetrics {
  hasData: boolean;
  isLoading: boolean;
  error: string | null;
  monthly: MonthlyPoint[];
  categories: CategorySlice[];
  totalNetSales: number;
  totalReceipts: number;
  avgReceipt: number;
  /** Revenue change of the latest month vs the one before it, in %. */
  momChangePct: number | null;
  periodMonths: number;
}

const PERIOD_MONTHS = 12;

export function useCompanySalesMetrics(organizationId?: string): CompanySalesMetrics {
  const { currentOrganization } = useAuth();
  const orgId = organizationId || currentOrganization?.organization_id;

  const [monthly, setMonthly] = useState<MonthlyPoint[]>([]);
  const [categories, setCategories] = useState<CategorySlice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMetrics = useCallback(async () => {
    if (!orgId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    const since = windowStart(new Date(), PERIOD_MONTHS);

    try {
      const [dailyResult, productResult] = await Promise.all([
        // `channel` JEST WYBIERANE I FILTROWANE, nie pomijane. Tabela trzyma na
        // każdy dzień wiersze źródeł ORAZ wiersz `all` będący ich sumą, więc
        // zapytanie bez tego filtru czytało jedno i drugie, a `foldMonthly`
        // dodawał całość do jej własnych części — obrót wychodził podwojony.
        // `all` jest tu właściwą odpowiedzią, bo tylko on obejmuje także kanały
        // historyczne, których nie ma w `sales_orders` (patrz `sales_agreguj`,
        // migracja 682). Podział na źródła to osobne pytanie i osobny filtr.
        supabase
          .from('lexshift_sales_daily')
          .select('business_date, channel, net_sales, receipts')
          .eq('organization_id', orgId)
          .eq('channel', KANAL_CALOSC)
          .gte('business_date', since)
          .order('business_date', { ascending: true }),
        supabase
          .from('lexshift_sales_product_daily')
          .select('category_name, net_sales')
          .eq('organization_id', orgId)
          .gte('business_date', since),
      ]);

      if (dailyResult.error) throw dailyResult.error;
      if (productResult.error) throw productResult.error;

      setMonthly(foldMonthly((dailyResult.data || []) as DailyRow[]));
      setCategories(foldCategories((productResult.data || []) as ProductRow[]));
    } catch (err) {
      console.error('Error fetching company sales metrics:', err);
      setError((err as Error).message);
      setMonthly([]);
      setCategories([]);
    } finally {
      setIsLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  const totalNetSales = monthly.reduce((sum, m) => sum + m.netSales, 0);
  const totalReceipts = monthly.reduce((sum, m) => sum + m.receipts, 0);

  return {
    hasData: monthly.length > 0,
    isLoading,
    error,
    monthly,
    categories,
    totalNetSales,
    totalReceipts,
    avgReceipt: totalReceipts > 0 ? totalNetSales / totalReceipts : 0,
    momChangePct: monthOverMonthChange(monthly),
    periodMonths: PERIOD_MONTHS,
  };
}
